import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { Typography, useTheme } from '@rossum/ui/material';
import {
  AreaPlot,
  ChartDataProvider,
  ChartsAxisHighlight,
  ChartsGrid,
  ChartsSurface,
  ChartsXAxis,
  ChartsYAxis,
  lineElementClasses,
  LinePlot,
} from '@rossum/ui/x-charts-pro';
import { EMPTY_PLACEHOLDER } from '../../../core/utils';
import { CommonXAxisProps } from '../../types';
import { ChartWrapper } from './ChartWrapper';
import { CustomLegend } from './CustomLegend';
import { CustomLegendTooltip } from './CustomLegendTooltip';
import { CustomTooltip } from './CustomTooltip';
import { getChartColors } from './styles';
import {
  CHART_HEIGHT,
  getCommonGradientAreaYAxisProps,
  getCommonYAxisProps,
} from './utils';

type AutomationRateChartProps = {
  automationRateTotal: string;
  automationRateChartDataset: {
    automatedCount: number[];
    nonAutomatedCount: number[];
    automationRate: number[];
    touchedCount: number[];
    touchlessCount: number[];
    touchlessRate: number[];
    date: string[];
  };
  xAxisProps: CommonXAxisProps;
};

const TOUCHLESS_RATE_SERIES_ID = 'touchlessRate';
const TOUCHLESS_RATE_YAXIS_ID = 'touchlessRateYAxis';
const AUTOMATION_RATE_SERIES_ID = 'automationRate';
const AUTOMATION_RATE_YAXIS_ID = 'automationRateAxis';

export const AutomationRateChart = ({
  automationRateTotal,
  automationRateChartDataset,
  xAxisProps,
}: AutomationRateChartProps) => {
  const theme = useTheme();
  const intl = useIntl();

  const areAllValuesZero =
    automationRateChartDataset.automationRate.every(value => value === 0) &&
    automationRateChartDataset.touchlessRate.every(value => value === 0);

  const { primary, secondary } = getChartColors(theme);

  const getCommonAutomationRateYaxisProps = (color: string) =>
    ({
      tickInterval: areAllValuesZero ? [0] : [25, 50, 75, 100],
      valueFormatter: (value: number) => `${value}%`,
      min: 0,
      max: 100,
      ...getCommonGradientAreaYAxisProps(color),
      ...getCommonYAxisProps(theme.palette.text.secondary),
    }) as const;

  return (
    <ChartWrapper>
      <Typography variant="h5" sx={{ span: { color: 'text.secondary' } }}>
        {automationRateTotal}{' '}
        <span>
          {intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.charts.automationRate.title',
          })}
        </span>
      </Typography>

      <ChartDataProvider
        height={CHART_HEIGHT}
        series={[
          {
            data: automationRateChartDataset.automationRate,
            area: true,
            type: 'line',
            label: intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.automationRate.series.automationRate',
            }),
            id: AUTOMATION_RATE_SERIES_ID,
            yAxisId: 'automationRateAxis',
            color: primary,
            valueFormatter: (value: number | null) =>
              R.isNumber(value) ? `${value}%` : EMPTY_PLACEHOLDER,
            curve: 'linear',
            labelMarkType: 'circle',
          },
          {
            data: automationRateChartDataset.touchlessRate,
            area: true,
            type: 'line',
            label: intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.automationRate.series.touchlessRate',
            }),
            id: TOUCHLESS_RATE_SERIES_ID,
            yAxisId: TOUCHLESS_RATE_YAXIS_ID,
            color: secondary,
            valueFormatter: (value: number | null) =>
              R.isNumber(value) ? `${value}%` : EMPTY_PLACEHOLDER,
            curve: 'linear',
            labelMarkType: 'circle',
          },
        ]}
        yAxis={[
          {
            id: AUTOMATION_RATE_YAXIS_ID,
            position: 'right',
            ...getCommonAutomationRateYaxisProps(primary),
          },
          {
            id: TOUCHLESS_RATE_YAXIS_ID,
            ...getCommonAutomationRateYaxisProps(secondary),
          },
        ]}
        xAxis={[
          {
            data: automationRateChartDataset.date,
            scaleType: 'band',
            categoryGapRatio: 0,
            ...xAxisProps,
          },
        ]}
        margin={{ left: 0, right: 0 }}
      >
        <ChartsSurface
          sx={{
            // The library auto-generates class names following the pattern Mui<Component>-series-<seriesId>
            [`& .${lineElementClasses.series}-${TOUCHLESS_RATE_SERIES_ID}`]: {
              stroke: `${secondary}`,
            },
            [`& .${lineElementClasses.series}-${AUTOMATION_RATE_SERIES_ID}`]: {
              stroke: `${primary}`,
            },
          }}
        >
          <ChartsGrid horizontal />
          <ChartsXAxis
            disableLine
            disableTicks
            tickLabelStyle={{ fill: theme.palette.text.secondary }}
          />
          <ChartsYAxis
            axisId={AUTOMATION_RATE_YAXIS_ID}
            disableLine
            disableTicks
            tickLabelStyle={{ fill: theme.palette.text.secondary }}
          />
          <ChartsAxisHighlight x="band" />
          <CustomTooltip
            getSeriesStyles={seriesId => {
              if (seriesId === AUTOMATION_RATE_SERIES_ID)
                return {
                  backgroundColor: primary,
                };

              if (seriesId === TOUCHLESS_RATE_SERIES_ID)
                return {
                  backgroundColor: secondary,
                };
              return {};
            }}
          />
          <AreaPlot />
          <LinePlot />
        </ChartsSurface>
        <CustomLegend
          primarySeries={intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.charts.automationRate.series.automationRate',
          })}
          secondarySeries={
            <CustomLegendTooltip
              label={intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.charts.automationRate.series.touchlessRate',
              })}
              tooltip={intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.charts.automationRate.series.touchlessRate.tooltip',
              })}
            />
          }
        />
      </ChartDataProvider>
    </ChartWrapper>
  );
};
